import React from "react";
import { Grid, Switch, TextField } from "@material-ui/core";
import { Field } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import { AppState } from "../../../store";
import {
  fillAgentFormField,
  setTheAgentForm,
} from "../store/agent-form.slice";
import useAuthority from "app/hooks/useAuthority";
import { ROLES } from "app/constants/roles";

interface ReservationInfoProps {
  errors?: any;
  touched?: any;
}

const ReservationInfo = ({
  errors = {},
  touched = {},
}: ReservationInfoProps) => {
  const dispatch = useDispatch();
  const agentForm = useSelector((state: AppState) => state.agents.agentForm);
  const { t } = useTranslation();

  const { hasUserAnyAuthority } = useAuthority();
  const isAdmin = hasUserAnyAuthority([ROLES.ROLE_ADMIN]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!isAdmin) return;

    dispatch(
      fillAgentFormField({
        value: e.target.value,
        field: e.target.name,
      })
    );
  };

  const preventMinus = (e: any) => {
    if (e.code === "Minus") {
      e.preventDefault();
    }
  };

  const hasError = (field: string) =>
    Boolean(touched[field] && errors[field]);

  const getError = (field: string) =>
    touched[field] && errors[field] ? errors[field] : "";

  return (
    <div>
      <Grid container spacing={4}>
        <Grid item xs={3}>
          <Field
            as={TextField}
            style={{ width: "100%" }}
            label={t(
              "app.main.agency.create.reservationMaxAdult",
              "Reservation Max. Adult"
            )}
            onChange={handleChange}
            value={agentForm.maxAdultAllowed}
            InputProps={{ inputProps: { min: 0 } }}
            variant="outlined"
            size="small"
            name="maxAdultAllowed"
            type="number"
            disabled={!isAdmin}
            required
            error={hasError("maxAdultAllowed")}
            helperText={getError("maxAdultAllowed")}
            onKeyPress={preventMinus}
            onKeyDown={(evt: React.KeyboardEvent<HTMLInputElement>) =>
              (evt.key === "e" || evt.key === "E") &&
              evt.preventDefault()
            }
          />
        </Grid>

        <Grid item xs={3}>
          <Field
            as={TextField}
            style={{ width: "100%" }}
            label={t(
              "app.main.agency.create.reservationMaxChild",
              "Reservation Max. Child"
            )}
            onChange={handleChange}
            value={agentForm.maxChildAllowed}
            InputProps={{ inputProps: { min: 0 } }}
            variant="outlined"
            size="small"
            name="maxChildAllowed"
            type="number"
            disabled={!isAdmin}
            required
            error={hasError("maxChildAllowed")}
            helperText={getError("maxChildAllowed")}
            onKeyPress={preventMinus}
            onKeyDown={(evt: React.KeyboardEvent<HTMLInputElement>) =>
              (evt.key === "e" || evt.key === "E") &&
              evt.preventDefault()
            }
          />
        </Grid>

        <Grid item xs={3}>
          <Field
            as={TextField}
            style={{ width: "100%" }}
            label={t(
              "app.main.agency.create.reservationMaxInf",
              "Reservation Max. Inf"
            )}
            onChange={handleChange}
            value={agentForm.maxInfantAllowed}
            InputProps={{ inputProps: { min: 0 } }}
            variant="outlined"
            size="small"
            name="maxInfantAllowed"
            type="number"
            disabled={!isAdmin}
            required
            error={hasError("maxInfantAllowed")}
            helperText={getError("maxInfantAllowed")}
            onKeyPress={preventMinus}
            onKeyDown={(evt: React.KeyboardEvent<HTMLInputElement>) =>
              (evt.key === "e" || evt.key === "E") &&
              evt.preventDefault()
            }
          />
        </Grid>

        <Grid item xs={3}>
          <div>
            {t(
              "app.main.agency.create.allowReservationInf",
              "Allow reservations only with Inf"
            )}
            :
          </div>

          <Switch
            checked={agentForm.allowInfantOnly}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (!isAdmin) return;

              dispatch(
                setTheAgentForm({
                  ...agentForm,
                  allowInfantOnly: e.target.checked,
                })
              );
            }}
            value={agentForm.allowInfantOnly}
            color="primary"
            name="switch"
            disabled={!isAdmin}
            inputProps={{ "aria-label": "primary checkbox" }}
          />
        </Grid>
      </Grid>
    </div>
  );
};

export default ReservationInfo;